import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Box,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Chip,
  Divider,
  IconButton,
  Collapse
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

const KPIDashboard = () => {
  const { currentUser } = useAuth();
  const [kpis, setKpis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Registration wizard state
  const [showWizard, setShowWizard] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [wizardLoading, setWizardLoading] = useState(false);
  const [wizardError, setWizardError] = useState(null);

  // Step data
  const [uploadedFile, setUploadedFile] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [userDescription, setUserDescription] = useState('');
  const [generatedConfig, setGeneratedConfig] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  // Check if user can register KPIs (admin or report, NOT manager)
  const canRegisterKPIs = currentUser?.role === 'admin' || currentUser?.role === 'report';

  const steps = [
    'Upload File',
    'Review Analysis',
    'Describe KPI',
    'Validate',
    'Confirm'
  ];

  // Get auth token
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('authToken');
    return {
      'Authorization': `Bearer ${token}`
    };
  }, []);

  // Fetch all KPIs
  const fetchKPIs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/kpi-dashboard/all`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error('Failed to fetch KPIs');
      }

      const data = await response.json();
      setKpis(data.kpis || []);
    } catch (err) {
      console.error('Error fetching KPIs:', err);
      setError('Failed to load KPIs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchKPIs();
  }, [fetchKPIs]);

  // Reset wizard state
  const resetWizard = () => {
    setActiveStep(0);
    setUploadedFile(null);
    setFileId(null);
    setAnalysis(null);
    setUserDescription('');
    setGeneratedConfig(null);
    setValidationResult(null);
    setWizardError(null);
    setWizardLoading(false);
  };

  // Handle file upload (Step 1)
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setWizardLoading(true);
    setWizardError(null);
    setUploadedFile(file);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_BASE}/api/kpi-dashboard/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Upload failed');
      }

      const data = await response.json();
      setFileId(data.file_id);

      // Automatically analyze the file
      const analyzeResponse = await fetch(`${API_BASE}/api/kpi-dashboard/analyze/${data.file_id}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (!analyzeResponse.ok) {
        const errData = await analyzeResponse.json();
        throw new Error(errData.error || 'Analysis failed');
      }

      const analysisData = await analyzeResponse.json();
      setAnalysis(analysisData.analysis);
      setActiveStep(1);
    } catch (err) {
      console.error('Upload/analyze error:', err);
      setWizardError(err.message);
    } finally {
      setWizardLoading(false);
    }
  };

  // Confirm analysis (Step 2)
  const handleConfirmAnalysis = () => {
    setActiveStep(2);
  };

  // Generate KPI definition (Step 3)
  const handleGenerateDefinition = async () => {
    if (!userDescription.trim()) {
      setWizardError('Please describe the KPI you want to track');
      return;
    }

    setWizardLoading(true);
    setWizardError(null);

    try {
      const response = await fetch(`${API_BASE}/api/kpi-dashboard/define`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file_id: fileId,
          user_description: userDescription,
          analysis: analysis
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Failed to generate definition');
      }

      const data = await response.json();
      setGeneratedConfig(data.config);

      // Validate the config
      const validateResponse = await fetch(`${API_BASE}/api/kpi-dashboard/validate/${fileId}`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ config: data.config })
      });

      if (!validateResponse.ok) {
        const errData = await validateResponse.json();
        throw new Error(errData.error || 'Validation failed');
      }

      const validationData = await validateResponse.json();
      setValidationResult(validationData);
      setActiveStep(3);
    } catch (err) {
      console.error('Definition/validation error:', err);
      setWizardError(err.message);
    } finally {
      setWizardLoading(false);
    }
  };

  // Register KPI (Step 4)
  const handleRegister = async () => {
    setWizardLoading(true);
    setWizardError(null);

    try {
      const response = await fetch(`${API_BASE}/api/kpi-dashboard/register`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          file_id: fileId,
          config: generatedConfig,
          extracted_value: validationResult.extracted_value
        })
      });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.error || 'Registration failed');
      }

      // Success!
      setActiveStep(4);

      // Refresh KPIs list
      await fetchKPIs();

      // Close wizard after a delay
      setTimeout(() => {
        setShowWizard(false);
        resetWizard();
      }, 2000);
    } catch (err) {
      console.error('Registration error:', err);
      setWizardError(err.message);
    } finally {
      setWizardLoading(false);
    }
  };

  // Try again (go back to step 3)
  const handleTryAgain = () => {
    setActiveStep(2);
    setGeneratedConfig(null);
    setValidationResult(null);
    setWizardError(null);
  };

  // Render KPI card
  const renderKPICard = (kpi) => {
    const getBorderColor = () => {
      switch (kpi.status_color) {
        case 'green': return '#4caf50';
        case 'red': return '#f44336';
        case 'yellow': return '#ff9800';
        default: return '#e0e0e0';
      }
    };

    return (
      <Card
        key={kpi.kpi_id}
        sx={{
          height: '100%',
          borderLeft: `4px solid ${getBorderColor()}`,
          transition: 'box-shadow 0.2s',
          '&:hover': {
            boxShadow: 3
          }
        }}
      >
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {kpi.report_type_name}
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
            {kpi.kpi_name}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color: getBorderColor(), mb: 2 }}>
            {kpi.formatted_value || 'N/A'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <AccessTimeIcon fontSize="small" />
            <Typography variant="caption">
              {kpi.last_updated ? new Date(kpi.last_updated).toLocaleDateString() : 'No data'}
            </Typography>
          </Box>
          {kpi.period_label && (
            <Chip
              label={kpi.period_label}
              size="small"
              sx={{ mt: 1 }}
              variant="outlined"
            />
          )}
        </CardContent>
      </Card>
    );
  };

  // Render analysis summary
  const renderAnalysisSummary = () => {
    if (!analysis) return null;

    return (
      <Box sx={{ textAlign: 'left' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          File Analysis Results
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {analysis.fileDescription}
        </Typography>

        {analysis.sheets?.map((sheet, idx) => (
          <Paper key={idx} variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Sheet: {sheet.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {sheet.description}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Header Row: {sheet.headerRow} | Data Starts: Row {sheet.dataStartRow} |
              Columns: {sheet.columns?.length || 0}
            </Typography>
            {sheet.columns && sheet.columns.length > 0 && (
              <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {sheet.columns.slice(0, 8).map((col, cidx) => (
                  <Chip
                    key={cidx}
                    label={`${col.name} (${col.dataType})`}
                    size="small"
                    variant="outlined"
                  />
                ))}
                {sheet.columns.length > 8 && (
                  <Chip label={`+${sheet.columns.length - 8} more`} size="small" />
                )}
              </Box>
            )}
          </Paper>
        ))}

        {analysis.suggestedKPIs && analysis.suggestedKPIs.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Suggested KPIs:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {analysis.suggestedKPIs.map((kpi, idx) => (
                <Chip key={idx} label={kpi} color="primary" variant="outlined" size="small" />
              ))}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

  // Render wizard content based on step
  const renderWizardContent = () => {
    switch (activeStep) {
      case 0: // Upload
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CloudUploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Upload a Report File
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Upload an Excel (.xlsx, .xls) or CSV file containing KPI data
            </Typography>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
              disabled={wizardLoading}
            >
              {wizardLoading ? 'Analyzing...' : 'Choose File'}
              <input
                type="file"
                hidden
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
              />
            </Button>
            {uploadedFile && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                Selected: {uploadedFile.name}
              </Typography>
            )}
          </Box>
        );

      case 1: // Review Analysis
        return (
          <Box sx={{ py: 2 }}>
            {renderAnalysisSummary()}
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button variant="outlined" onClick={() => { resetWizard(); }}>
                Upload Different File
              </Button>
              <Button variant="contained" onClick={handleConfirmAnalysis}>
                Looks Good - Continue
              </Button>
            </Box>
          </Box>
        );

      case 2: // Describe KPI
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Describe the KPI you want to track
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Describe in plain English what metric you want to extract from this file.
              For example: "Track the total sales amount from the 'Revenue' column" or
              "Calculate the average fill rate percentage from the last row"
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="e.g., I want to track the SKU in-stock percentage, which is in the 'In Stock %' column of the summary sheet..."
              value={userDescription}
              onChange={(e) => setUserDescription(e.target.value)}
              sx={{ mb: 3 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button variant="outlined" onClick={() => setActiveStep(1)}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleGenerateDefinition}
                disabled={wizardLoading || !userDescription.trim()}
              >
                {wizardLoading ? 'Generating...' : 'Generate KPI Definition'}
              </Button>
            </Box>
          </Box>
        );

      case 3: // Validate
        return (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Validation Result
            </Typography>
            {validationResult && (
              <>
                <Paper variant="outlined" sx={{ p: 3, mb: 3, display: 'inline-block' }}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    Based on your description, the KPI:
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
                    "{validationResult.kpi_name}"
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    has a current value of:
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {validationResult.formatted_value}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    ({validationResult.values_found} values found, using {validationResult.aggregation})
                  </Typography>
                </Paper>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Does this look correct?
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                  <Button variant="outlined" onClick={handleTryAgain}>
                    Try Again
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={handleRegister}
                    disabled={wizardLoading}
                  >
                    {wizardLoading ? 'Saving...' : 'Confirm & Save'}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        );

      case 4: // Success
        return (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
              KPI Registered Successfully!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              Your new KPI tile will appear on the dashboard.
            </Typography>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          KPI Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchKPIs}
            disabled={loading}
          >
            Refresh
          </Button>
          {canRegisterKPIs && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { resetWizard(); setShowWizard(true); }}
            >
              Add KPI
            </Button>
          )}
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Registration Wizard */}
      {showWizard && canRegisterKPIs && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Register New KPI
            </Typography>
            <IconButton onClick={() => { setShowWizard(false); resetWizard(); }}>
              <ExpandLessIcon />
            </IconButton>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {wizardError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setWizardError(null)}>
              {wizardError}
            </Alert>
          )}

          {wizardLoading && activeStep < 4 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {!wizardLoading && renderWizardContent()}
        </Paper>
      )}

      {/* KPI Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : kpis.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No KPIs configured yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {canRegisterKPIs
              ? 'Click "Add KPI" to register your first KPI from a report file.'
              : 'Contact an administrator to set up KPI tracking.'}
          </Typography>
          {canRegisterKPIs && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => { resetWizard(); setShowWizard(true); }}
            >
              Add Your First KPI
            </Button>
          )}
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {kpis.map((kpi) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={kpi.kpi_id}>
              {renderKPICard(kpi)}
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
};

export default KPIDashboard;
